import { joinIds, splitIds, updateWithToast, updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { toast } from 'react-hot-toast';

import type { CampaignOverrideMode } from '@poseidon/components/Input2';
import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import {
  Button,
  ButtonGroup,
  CampaignTemplateFilterSelectWithOverride,
  FilterSelect,
  OwnerFilterSelect,
  StageFilterSelect,
} from '@poseidon/components/Input2';
import { DeleteCadencePopup, MarkDonePopup } from '@poseidon/components/Popup';
import { Arrows, Development, General, Media, Users } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';
import { hasItems } from '@poseidon/shared-utils';

import type { SelectionBarAction } from '~/components/SelectedPopup/SelectionBarActions';
import type { SkippedContactsCategories } from '~/components/SkippedContactsPopup';
import { useAiStepBulkCreateMutation } from '@/hooks/trpc/useAiStepMutation';
import {
  useBulkChangeCadenceMutation,
  useCadenceChangeUserBulkMutation,
  useCadenceDeleteAllMutation,
  useCadencePauseAllMutation,
  useCadenceResumeAllMutation,
} from '@/hooks/trpc/useCadenceMutation';
import {
  useContactBulkAckInteraction,
  useContactBulkSetStageMutation,
  useEnhanceContactMutation,
  useGenerateDossiersBulkMutation,
  useResearchFromLinkedInMutation,
} from '@/hooks/trpc/useContactMutation';
import { useGlobalReminderDeleteBulk } from '@/hooks/trpc/useGlobalReminders';
import { useSequenceAssignMutation } from '@/hooks/trpc/useSequenceMutation';
import { useBulkAddTagToContactsMutation } from '@/hooks/trpc/useTagMutation';
import { useUpdateTeamData } from '@/hooks/trpc/useUserMutation';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { BulkCampaignConfirm } from '~/components/CampaignOwner/BulkCampaignConfirm';
import { campaignOf } from '~/components/CampaignOwner/campaignOwner';
import { useActingPerson } from '~/components/CampaignOwner/useCampaignOwner';
import { ExportContactsModal } from '~/components/SelectedPopup/ExportContactsModal';
import { SelectionBarActions } from '~/components/SelectedPopup/SelectionBarActions';
import { estimateLabeledWidth, ICON_BUTTON_WIDTH } from '~/components/SelectedPopup/selectionBarLayout';
import SelectPopupContainer from '~/components/SelectedPopup/SelectPopupContainer';
import { SkippedContactsPopup } from '~/components/SkippedContactsPopup';
import { TagFilterSelectNextJS } from '~/components/TagFilterSelectNextJS';
import { useIsAdminLike } from '~/hooks/useSettingsPermissions';

export type SelectionActionId =
  | 'reminderDone'
  | 'acknowledge'
  | 'generateDossiers'
  | 'exportCsv'
  | 'enhance'
  | 'resume'
  | 'pause'
  | 'takeAction'
  | 'owner'
  | 'campaign'
  | 'sequence'
  | 'stageTag';

/**
 * Actions that stay visible longest when the bar is narrow. Reminder / Acknowledge only appear when
 * the selection holds reminders / views, so listing them is safe on every page.
 */
export const DEFAULT_PRIMARY_ACTIONS: SelectionActionId[] = [
  'reminderDone',
  'acknowledge',
  'takeAction',
  'campaign',
  'sequence',
];

/** Estimated widths of icon-only selects (icon + chevron), for the bar's collapse math. */
const SELECT_ICON_WIDTH = 44;
const ONLY_NEW_SELECT_WIDTH = SELECT_ICON_WIDTH;

type ContactSelectedPopupProps = {
  teamId: string;
  userId: string;
  isPremium: boolean;
  creditsRemaining: number;
  /** Which actions a page wants kept visible when space runs out. Defaults to DEFAULT_PRIMARY_ACTIONS. */
  primaryActions?: SelectionActionId[];
};

export const ContactSelectedPopup = (props: ContactSelectedPopupProps) => {
  const [query] = useRouterQuery();
  // POS-2482: the durable record of a bulk add's refusals, categorized — the toasts are only
  // attention-getters; this popup persists until dismissed.
  const [skipped, setSkipped] = React.useState<SkippedContactsCategories | null>(null);

  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();

  const onAddSkippedAnyway = (contactPublicIds: string[]) =>
    aiStepBulkCreateMutation.mutateAsync({
      teamId: props.teamId,
      userId: props.userId,
      data: { contactIds: contactPublicIds, type: 'message' },
      onlyNewPeople: false,
    });

  return (
    <>
      <AnimatePresence>
        {hasItems(query.selectedIds) && (
          <ContactSelectedPopupWithIds {...props} selectedIds={query.selectedIds ?? []} onSkipped={setSkipped} />
        )}
      </AnimatePresence>
      {skipped != null && (
        <SkippedContactsPopup
          teamId={props.teamId}
          skipped={skipped}
          onClose={() => setSkipped(null)}
          onAddAnyway={onAddSkippedAnyway}
        />
      )}
    </>
  );
};

interface ContactSelectedPopupWithIdsProps extends ContactSelectedPopupProps {
  selectedIds: string[];
  onSkipped: (skipped: SkippedContactsCategories) => void;
}
export const ContactSelectedPopupWithIds = ({
  teamId,
  userId,
  isPremium,
  creditsRemaining,
  selectedIds,
  onSkipped,
  primaryActions = DEFAULT_PRIMARY_ACTIONS,
}: ContactSelectedPopupWithIdsProps) => {
  const [loading, setLoading] = React.useState<string>();
  const [shouldDeleteReminders, setShouldDeleteReminders] = React.useState(false);
  const [shouldResolveEvents, setShouldResolveEvents] = React.useState(false);
  const [shouldDeleteCadences, setShouldDeleteCadences] = React.useState(false);

  const [auth] = useAuthContext();
  const authedUserTeam = 'userTeam' in auth ? auth.userTeam : null;
  const onlyNewPeople = authedUserTeam?.takeActionOnlyNewPeople ?? true;
  const campaignOverrideMode: CampaignOverrideMode = authedUserTeam?.campaignOverrideMode ?? 'smart_override';
  // POS-2455 "Resume from last left off" (default ON). Session-local — unlike
  // the override mode it is not persisted on user_team_data; re-sending is a
  // deliberate per-action choice, not a preference.
  const [resumePriorSteps, setResumePriorSteps] = React.useState(true);

  const utils = api.useUtils();
  const updateTeamDataMutation = useUpdateTeamData();
  const setOnlyNewPeople = (next: boolean) => {
    if (next === onlyNewPeople) return;
    // Optimistically flip the chevron — feels instant. The shared
    // useUpdateTeamData hook invalidates user.me on success (which
    // reconciles against the server) and we rollback on error.
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, takeActionOnlyNewPeople: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { takeActionOnlyNewPeople: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };
  const setCampaignOverrideMode = (next: CampaignOverrideMode) => {
    if (next === campaignOverrideMode) return;
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, campaignOverrideMode: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { campaignOverrideMode: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };

  const isAdminLike = useIsAdminLike();
  const teamUserOptionsQuery = api.team.teamUserOptions.useQuery({ teamId }, { enabled: isAdminLike });
  const userOptions = teamUserOptionsQuery.data ?? [];

  const cadenceTemplatesQuery = api.cadenceTemplate.namesWithOwner.useQuery({ teamId, pagination: { limit: 5000 } });
  const templates =
    cadenceTemplatesQuery.data?.data.map((d) => ({
      id: d.id,
      name: d.name,
      isMine: d.userId === userId,
      errorCount: d.errorSteps?.length ?? 0,
      ownerName: d.ownerName,
      ownerProfilePicture: d.ownerProfilePicture,
    })) ?? [];

  const stagesQuery = api.stage.all.useQuery({ teamId });
  const stages = stagesQuery.data?.stages ?? [];

  // Enrolling more than one person asks first: into whose campaign, sending from whose LinkedIn.
  // Both paths (direct start and staging intake) enroll as the person you're acting as, so that's
  // the sender (cadenceBulk.bulkChangeCadence: startCadenceWithContactId / pgCandidateIntakeBulk).
  const acting = useActingPerson();
  const [enrollConfirm, setEnrollConfirm] = React.useState<string | null>(null);
  const enrollTemplate = cadenceTemplatesQuery.data?.data.find((d) => d.id === enrollConfirm);
  const enrollOwner =
    enrollTemplate == null
      ? null
      : {
          userId: enrollTemplate.userId,
          name: enrollTemplate.ownerName,
          profilePicture: enrollTemplate.ownerProfilePicture,
        };

  // Assigning people to a sequence is done from here (search selection), gated on the flag.
  const sequencesEnabled = authedUserTeam?.sequenceEnabled === true;
  const sequencesQuery = api.sequence.list.useQuery({ teamId }, { enabled: sequencesEnabled });
  const sequences = sequencesQuery.data?.sequences ?? [];

  const [query, setQuery] = useRouterQuery();
  const enhanceMutation = useEnhanceContactMutation();
  const generateDossiersMutation = useGenerateDossiersBulkMutation();
  const [showExportModal, setShowExportModal] = React.useState(false);
  const researchMutation = useResearchFromLinkedInMutation();
  const bulkChangeCadenceMutation = useBulkChangeCadenceMutation();
  const cadenceDeleteAllMutation = useCadenceDeleteAllMutation();
  const cadenceChangeUserMutation = useCadenceChangeUserBulkMutation();
  const cadencePauseAllMutation = useCadencePauseAllMutation();
  const cadenceResumeAllMutation = useCadenceResumeAllMutation();
  const contactBulkAckInteractionMutation = useContactBulkAckInteraction();
  const deleteRemindersMutation = useGlobalReminderDeleteBulk();
  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();
  const bulkSetStageMutation = useContactBulkSetStageMutation();
  const bulkAddTagMutation = useBulkAddTagToContactsMutation();
  const sequenceAssignMutation = useSequenceAssignMutation();

  const splitSelectedIds = selectedIds.map((c) => splitIds(c));

  const onClose = () => setQuery((q) => ({ ...q, selectedIds: undefined }));
  useEscStack('ContactSelectedPopup', ESC_PRIORITY.ACTION_BAR, onClose);

  const onDelete = async () => {
    const idObj = splitIds(query?.id ?? '');
    const didSelectId = splitSelectedIds.find((s) => s.cadenceId == idObj.cadenceId) != null;

    setShouldDeleteCadences(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Cadences deleted',
      setQuery,
      setLoading,
      onSuccessQuery:
        idObj && didSelectId
          ? { selectedIds: undefined, id: joinIds({ contactId: idObj.contactId }) }
          : { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceDeleteAllMutation.mutateAsync({
          teamId: teamId,
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onChangeOwner = (userId: string) =>
    updateWithToast({
      loading: 'Changing Owner...',
      success: userId === '' ? 'Owner removed' : 'Owner changed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceChangeUserMutation.mutateAsync({
          data: {
            cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
            userId: userId || null,
          },
        }),
    });

  const onEnhance = () => {
    const contactIds = selectedIds.map((c) => splitIds(c).contactId).filter((a) => a != null);
    const willEnrich = isPremium && creditsRemaining > 0;

    return updateWithToast({
      loading: willEnrich ? 'Refreshing & enriching...' : 'Refreshing from LinkedIn...',
      success: willEnrich
        ? 'Contacts refreshed — enrichment running in background'
        : 'Contacts refreshed from LinkedIn',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: async () => {
        const researchResults = await Promise.all(
          contactIds.map((contactId) =>
            researchMutation
              .mutateAsync({ contactId, teamId: teamId, userId: userId })
              .then((r) => {
                return { ok: true as const, contactId, r };
              })
              .catch((e: unknown) => {
                return { ok: false as const, contactId, error: e };
              }),
          ),
        );

        const experienceThrottledCount = researchResults.filter(
          (res) => res.ok && res.r?.throttledSections?.includes('experience'),
        ).length;
        // Empty-without-throttle is a different condition (truly private / no job history).
        const emptyExperienceCount = researchResults.filter(
          (res) =>
            res.ok &&
            res.r?.experienceFetchedCount === 0 &&
            !res.r?.throttledSections?.includes('experience') &&
            !res.r?.skipped,
        ).length;
        const skippedFreshCount = researchResults.filter((res) => res.ok && res.r?.skipped).length;

        if (experienceThrottledCount > 0) {
          toast(
            experienceThrottledCount === 1
              ? "LinkedIn is currently rate-limiting this profile's experience section. We'll automatically re-fetch in a few minutes — your existing experiences are preserved."
              : `LinkedIn is currently rate-limiting the experience section for ${experienceThrottledCount} contacts. We'll automatically re-fetch in a few minutes — existing experiences are preserved.`,
            { duration: 8000 },
          );
        } else if (emptyExperienceCount > 0) {
          toast(
            emptyExperienceCount === 1
              ? 'LinkedIn returned no work history for 1 contact (private profile or no public job history). Existing experiences were kept.'
              : `LinkedIn returned no work history for ${emptyExperienceCount} contacts (private profiles or no public job history). Existing experiences were kept.`,
            { duration: 6000 },
          );
        } else if (skippedFreshCount > 0) {
          toast(
            skippedFreshCount === 1
              ? 'Contact was refreshed in the last 24h — skipped re-fetching.'
              : `${skippedFreshCount} contacts were refreshed in the last 24h — skipped re-fetching.`,
            { duration: 5000 },
          );
        }
        if (willEnrich) {
          const enhanceResults = await Promise.all(
            contactIds.map((contactId) =>
              enhanceMutation
                .mutateAsync({ contactId })
                .then((r) => {
                  return { ok: true as const, contactId, r };
                })
                .catch((e: unknown) => {
                  return { ok: false as const, contactId, error: e };
                }),
            ),
          );

          // Enhance re-verifies each contact on LinkedIn; when a profile is confirmed
          // dead and unrepairable, the server removes that contact in the same call and
          // reports `deleted`. Nothing to delete client-side — just report how many were
          // removed. (List invalidation is handled by the enhance mutation's onSuccess.)
          const deletedCount = enhanceResults.filter((res) => res.ok && res.r.deleted).length;
          if (deletedCount > 0) {
            toast(
              deletedCount === 1
                ? 'Removed 1 contact whose LinkedIn profile no longer exists on LinkedIn.'
                : `Removed ${deletedCount} contacts whose LinkedIn profiles no longer exist on LinkedIn.`,
              { duration: 6000 },
            );
          }
        }
      },
    });
  };

  const onBulkStartCadence = async (cadenceTemplateId: string) => {
    const isRemoving = cadenceTemplateId === '';
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);

    if (isRemoving) {
      return updateWithToast({
        loading: 'Removing from campaign...',
        success: 'Removed from campaign',
        setQuery,
        setLoading,
        onSuccessQuery: { selectedIds: undefined },
        onErrorQuery: { selectedIds: undefined },
        mutationFnAsync: () =>
          bulkChangeCadenceMutation.mutateAsync({
            teamId,
            cadenceTemplateId: null,
            contactIds,
          }),
      });
    }

    const result = await updateWithToastPromise({
      loading: 'Changing cadence...',
      success: (data) => (data != null && data.staged > 0 ? `${data.staged} added` : 'Cadences changed'),
      mutationFnAsync: () =>
        bulkChangeCadenceMutation.mutateAsync({
          teamId,
          cadenceTemplateId,
          contactIds,
          resumePriorSteps,
        }),
    });

    // POS-2482: refusals get a one-line ⚠️ toast as the attention-getter, but the durable record is
    // the SkippedContactsPopup — who was skipped, why (in sequence "X" / everything already sent /
    // previously rejected by the audience filter — POS-2453 / POS-2455 / POS-2407), and what to do.
    const skippedInSequence = result?.skippedInSequence ?? [];
    const skippedAlreadySent = result?.skippedAlreadySentContactPublicIds ?? [];
    const skippedRejected = result?.skippedPermanentlyRejectedContactPublicIds ?? [];
    const totalSkipped = skippedInSequence.length + skippedAlreadySent.length + skippedRejected.length;
    if (totalSkipped > 0) {
      toast(`${totalSkipped} contact${totalSkipped === 1 ? '' : 's'} skipped`, { icon: '⚠️', duration: 6000 });
      onSkipped({
        inSequence: skippedInSequence,
        alreadySent: skippedAlreadySent,
        previouslyRejected: skippedRejected,
      });
    }

    setQuery((q) => ({ ...q, selectedIds: undefined }));
  };

  // Removing needs no confirm (nobody is enrolled); enrolling one person is what the pick says.
  const onPickCampaign = (cadenceTemplateId: string) => {
    const contactCount = splitSelectedIds.filter((c) => c.contactId != null).length;
    if (cadenceTemplateId !== '' && contactCount > 1) setEnrollConfirm(cadenceTemplateId);
    else void onBulkStartCadence(cadenceTemplateId);
  };

  const onBulkAddToSequence = async (sequenceId: string) => {
    if (sequenceId === '') return;
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a): a is string => a != null);
    let result;
    try {
      result = await updateWithToastPromise({
        loading: 'Adding to sequence…',
        success: (data) => (data.skipped.length > 0 ? `${data.assigned} added to sequence` : 'Added to sequence'),
        mutationFnAsync: () => sequenceAssignMutation.mutateAsync({ teamId, sequenceId, contactIds }),
      });
    } catch {
      // updateWithToastPromise already toasted the error; keep the pre-POS-2481 behavior of
      // clearing the selection on failure (updateWithToast's onErrorQuery did this) rather
      // than letting the rejection escape the onChange handler.
      setQuery((q) => ({ ...q, selectedIds: undefined }));
      return;
    }
    // POS-2481: the door check refused these (live campaign / other sequence / staged candidate) —
    // say who was skipped and why, mirroring the campaign-direction skip toasts above.
    if (result.skipped.length > 0) {
      const reasons = [...new Set(result.skipped.map((s) => s.reason))].join('; ');
      toast(`${result.skipped.length} contact${result.skipped.length === 1 ? '' : 's'} skipped — ${reasons}`, {
        icon: '⚠️',
        duration: 6000,
      });
    }
    setQuery((q) => ({ ...q, selectedIds: undefined }));
  };

  // When the selection flow was opened from a campaign switch
  // (/prospects?switchId=…), the primary action is "add to switch" — the switch
  // routes each contact into a campaign by attribute, so there's no campaign to
  // pick here. See AddPeopleToSwitchActions.
  const switchId = query.switchId;
  const switchQuery = api.campaignSwitch.get.useQuery(
    { teamId, switchId: switchId ?? '' },
    { enabled: Boolean(switchId) },
  );
  const intakeIntoSwitchMutation = api.campaignSwitch.intakeIntoSwitch.useMutation({
    onSuccess: () => {
      if (switchId == null) return;
      void utils.campaignSwitch.stagingList.invalidate({ teamId, switchId });
      void utils.campaignSwitch.candidateCounts.invalidate({ teamId, switchId });
    },
  });

  const onAddToSwitch = async () => {
    if (switchId == null) return;
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a): a is string => a != null);
    if (contactIds.length === 0) return;
    const result = await updateWithToastPromise({
      loading: 'Adding to switch...',
      success: (data) =>
        data.inSequence > 0
          ? `${data.staged} added — routing runs after research`
          : 'Added — routing runs after research',
      mutationFnAsync: () => intakeIntoSwitchMutation.mutateAsync({ teamId, switchId, contactIds }),
    });
    // POS-2453: contacts a live sequence is working were refused — a separate
    // warning, like the campaign path, so a refusal never hides under a green check.
    if (result.inSequence > 0) {
      toast(`${result.inSequence} contact${result.inSequence === 1 ? '' : 's'} skipped — already in a sequence`, {
        icon: '⚠️',
        duration: 6000,
      });
    }
    setQuery((q) => ({ ...q, selectedIds: undefined }));
  };

  const onBulkSetStage = (stageId: string) =>
    updateWithToast({
      loading: 'Changing stage...',
      success: 'Stage updated',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkSetStageMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          stageId: stageId || null,
        }),
    });

  const onBulkAddTag = (tagId: string) =>
    updateWithToast({
      loading: 'Adding tag...',
      success: 'Tag added',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkAddTagMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          tagId,
        }),
    });

  const onResume = async () => {
    await updateWithToast({
      loading: 'Resuming...',
      success: 'Cadences resumed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceResumeAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onPause = () =>
    updateWithToast({
      loading: 'Pausing...',
      success: 'Cadences paused',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadencePauseAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });

  const onAddToAiMessage = async () => {
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);
    setQuery((q) => ({ ...q, selectedIds: undefined }));
    const toastId = toast.loading('Adding to Take Action...');
    try {
      const result = await aiStepBulkCreateMutation.mutateAsync({
        teamId: teamId,
        userId: userId,
        data: { contactIds, type: 'message' },
        onlyNewPeople,
      });
      // When previously-added contacts were skipped, the SkippedContactsPopup
      // tells the full story — don't double up with a toast.
      if (result.skippedContactIds.length > 0) {
        toast.dismiss(toastId);
        onSkipped({ previouslyAdded: result.skippedContactIds });
      } else {
        const skippedReasons: string[] = [];
        if (result.skippedDisqualified > 0) {
          skippedReasons.push(`${result.skippedDisqualified} disqualified`);
        }
        if (result.skippedAlreadyInQueue > 0) {
          skippedReasons.push(`${result.skippedAlreadyInQueue} already in queue`);
        }
        const skippedMessage = skippedReasons.length > 0 ? `. Skipped: ${skippedReasons.join(', ')}` : '';
        toast.success(`Added ${result.created} to Take Action${skippedMessage}`, { id: toastId });
      }
    } catch {
      toast.error('Failed to add to Take Action', { id: toastId });
    }
  };

  const onAcknowledge = async () => {
    setShouldResolveEvents(false);
    await updateWithToast({
      loading: 'Acknowledging...',
      success: 'Acknowledged views',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        contactBulkAckInteractionMutation.mutateAsync({
          viewIds: splitSelectedIds.map((c) => c.interactionId).filter((a) => a != null),
        }),
    });
  };

  const onDeleteReminders = async () => {
    setShouldDeleteReminders(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Reminders deleted',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        deleteRemindersMutation.mutateAsync({
          globalReminderIds: splitSelectedIds.map((c) => c.reminderId).filter((a) => a != null),
        }),
    });
  };

  const cadenceIdsLength = splitSelectedIds.filter((a) => a.cadenceId != null).length ?? 0;
  const contactIdsLength = splitSelectedIds.filter((a) => a.contactId != null).length ?? 0;
  const selectedContactIds = splitSelectedIds.map((a) => a.contactId).filter((a): a is string => a != null);

  // Dossier synthesis runs on the clicking user's Anthropic key — hide the
  // button when it isn't configured (flag-gating rule: never surface an
  // affordance that can only error). Tavily is NOT part of this gate: web
  // research runs on the org key, which the user can't configure either way,
  // and the route fails loudly if it's missing server-side.
  const canGenerateDossiers = authedUserTeam?.anthropicApiKey != null && authedUserTeam.anthropicApiKey.length > 0;

  const onGenerateDossiers = () =>
    updateWithToast({
      loading: 'Queueing dossier research…',
      success: 'Dossier generation queued',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      mutationFnAsync: async () => {
        const result = await generateDossiersMutation.mutateAsync({
          teamId,
          contactIds: selectedContactIds,
        });
        if (result.queued === 0 && result.skippedFresh > 0) {
          toast(`All ${result.skippedFresh} dossiers are fresh (under ~6 months) — nothing to regenerate.`);
        } else if (result.skippedFresh > 0) {
          toast(`Queued ${result.queued} — skipped ${result.skippedFresh} with a fresh dossier.`);
        }
      },
    });
  const reminderIdsLength = splitSelectedIds.filter((a) => a.reminderId != null).length ?? 0;
  const interactionIdsLength = splitSelectedIds.filter((a) => a.interactionId != null).length ?? 0;

  const isPrimary = (id: SelectionActionId) => primaryActions.includes(id);
  const actions: SelectionBarAction[] = [];

  if (reminderIdsLength > 0) {
    actions.push({
      id: 'reminderDone',
      kind: 'button',
      priority: 0,
      primary: isPrimary('reminderDone'),
      label: 'Mark Reminder as Done',
      icon: <General.Check />,
      onClick: () => setShouldDeleteReminders(true),
    });
  }

  if (interactionIdsLength > 0) {
    actions.push({
      id: 'acknowledge',
      kind: 'button',
      priority: 1,
      primary: isPrimary('acknowledge'),
      label: 'Acknowledge',
      icon: <General.Eye />,
      onClick: () => setShouldResolveEvents(true),
    });
  }

  if (contactIdsLength > 0 && canGenerateDossiers) {
    actions.push({
      id: 'generateDossiers',
      kind: 'button',
      priority: 20,
      primary: isPrimary('generateDossiers'),
      label: 'Generate dossiers',
      icon: generateDossiersMutation.isPending ? <WaveSpinner /> : <General.SearchSm />,
      disabled: generateDossiersMutation.isPending,
      onClick: () => void onGenerateDossiers(),
      tooltip: `Research + generate a dossier for ${contactIdsLength === 1 ? 'this contact' : `each of the ${contactIdsLength} contacts`} (skips dossiers newer than ~6 months)`,
    });
  }

  if (contactIdsLength > 0) {
    actions.push({
      id: 'exportCsv',
      kind: 'button',
      priority: 22,
      primary: isPrimary('exportCsv'),
      label: 'Export CSV',
      icon: <General.Download01 />,
      onClick: () => setShowExportModal(true),
      tooltip: `Export ${contactIdsLength === 1 ? 'this contact' : `${contactIdsLength} contacts`} as CSV — pick columns incl. dossier sections`,
    });
    actions.push({
      id: 'enhance',
      kind: 'button',
      priority: 21,
      primary: isPrimary('enhance'),
      label: 'Enhance',
      icon: enhanceMutation.isPending || researchMutation.isPending ? <WaveSpinner /> : <Arrows.RefreshCw04 />,
      disabled: enhanceMutation.isPending || researchMutation.isPending,
      onClick: () => void onEnhance(),
      tooltip:
        isPremium && creditsRemaining > 0
          ? `Refresh & enrich ${contactIdsLength} ${contactIdsLength === 1 ? 'contact' : 'contacts'} (uses up to ${Math.min(contactIdsLength, creditsRemaining).toLocaleString()} of ${creditsRemaining.toLocaleString()} credits)`
          : `Refresh LinkedIn data only — no enrichment credits remaining`,
    });
  }

  if (cadenceIdsLength > 0) {
    actions.push({
      id: 'resume',
      kind: 'button',
      priority: 30,
      primary: isPrimary('resume'),
      iconOnly: true,
      label: `Resume${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`,
      icon: <Media.PlayCircle />,
      onClick: () => void onResume(),
    });
    actions.push({
      id: 'pause',
      kind: 'button',
      priority: 31,
      primary: isPrimary('pause'),
      iconOnly: true,
      label: `Pause${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`,
      icon: <Media.PauseCircle />,
      onClick: () => void onPause(),
    });
  }

  if (contactIdsLength > 0) {
    actions.push({
      id: 'takeAction',
      kind: 'control',
      priority: 2,
      primary: isPrimary('takeAction'),
      labeledWidth: ONLY_NEW_SELECT_WIDTH + estimateLabeledWidth('Add to Take Action'),
      iconWidth: ONLY_NEW_SELECT_WIDTH + ICON_BUTTON_WIDTH,
      render: (showText) => (
        <ButtonGroup size="sm">
          <FilterSelect
            value={onlyNewPeople ? 'only_new' : 'everyone'}
            options={[
              {
                value: 'only_new',
                label: 'Only new people',
                description: 'Skip contacts you have ever added to Take Action',
                icon: <Users.UserPlus01 />,
              },
              {
                value: 'everyone',
                label: 'Everyone',
                description: 'Add all selected contacts, even ones you previously added',
                icon: <Users.Users01 />,
              },
            ]}
            onChange={(v) => setOnlyNewPeople(v === 'only_new')}
            tooltip={
              onlyNewPeople
                ? 'Only contacts you have never added to Take Action will be added'
                : 'All selected contacts will be added, even ones you previously added to Take Action'
            }
            filterable={false}
            showText={false}
            autoWidth
          />
          <Button
            onClick={onAddToAiMessage}
            icon={<General.Zap />}
            tooltip="Add to Take Action"
            aria-label="Add to Take Action">
            {showText ? 'Add to Take Action' : undefined}
          </Button>
        </ButtonGroup>
      ),
    });
  }

  if (cadenceIdsLength > 0 && isAdminLike) {
    actions.push({
      id: 'owner',
      kind: 'control',
      priority: 25,
      primary: isPrimary('owner'),
      labeledWidth: 110,
      iconWidth: SELECT_ICON_WIDTH,
      render: (showText) => (
        <OwnerFilterSelect
          userOptions={userOptions}
          value={undefined}
          onChange={onChangeOwner}
          placeholder="Owner"
          size="sm"
          tooltip="Change owner"
          showText={showText}
        />
      ),
    });
  }

  if (contactIdsLength > 0 && switchId == null) {
    actions.push({
      id: 'campaign',
      kind: 'control',
      priority: 3,
      primary: isPrimary('campaign'),
      // Override-mode + resume selects (always icon-only) beside the campaign trigger.
      labeledWidth: 2 * SELECT_ICON_WIDTH + estimateLabeledWidth('Campaign') + 18,
      iconWidth: 3 * SELECT_ICON_WIDTH,
      render: (showText) => (
        <CampaignTemplateFilterSelectWithOverride
          templates={templates}
          includeRemoveOption
          renderOwnerIcon={({ ownerProfilePicture, ownerName }) => (
            <TeamUserAvatar
              userTeam={{ profilePicture: ownerProfilePicture, userTeamName: ownerName }}
              className="size-full"
              shape="circle"
            />
          )}
          onChange={onPickCampaign}
          overrideMode={campaignOverrideMode}
          onOverrideModeChange={setCampaignOverrideMode}
          resumePriorSteps={resumePriorSteps}
          onResumePriorStepsChange={setResumePriorSteps}
          placeholder="Campaign"
          size="sm"
          showText={showText}
        />
      ),
    });
  }

  if (contactIdsLength > 0 && switchId == null && sequencesEnabled && sequences.length > 0) {
    actions.push({
      id: 'sequence',
      kind: 'control',
      priority: 4,
      primary: isPrimary('sequence'),
      labeledWidth: estimateLabeledWidth('Sequence') + 18,
      iconWidth: SELECT_ICON_WIDTH,
      render: (showText) => (
        <FilterSelect
          icon={<Development.Dataflow04 />}
          placeholder="Sequence"
          value=""
          options={sequences.map((s) => ({ value: s.id, label: s.name }))}
          onChange={onBulkAddToSequence}
          size="sm"
          popoverPlacement="top-start"
          showText={showText}
          tooltip={showText ? undefined : 'Sequence'}
        />
      ),
    });
  }

  if (contactIdsLength > 0 && switchId != null) {
    actions.push({
      id: 'switch',
      kind: 'button',
      priority: 2,
      primary: true,
      color: 'blue',
      label: `Add to switch${switchQuery.data?.name ? ` "${switchQuery.data.name}"` : ''}`,
      icon: <General.Plus />,
      onClick: () => void onAddToSwitch(),
    });
  }

  if (contactIdsLength > 0) {
    actions.push({
      id: 'stageTag',
      kind: 'control',
      priority: 26,
      primary: isPrimary('stageTag'),
      labeledWidth: 2 * SELECT_ICON_WIDTH,
      iconWidth: 2 * SELECT_ICON_WIDTH,
      render: () => (
        <ButtonGroup size="sm">
          <StageFilterSelect
            stages={stages}
            includeRemoveOption
            onChange={onBulkSetStage}
            placeholder="Stage"
            showText={false}
          />
          <TagFilterSelectNextJS
            teamId={teamId}
            onChange={onBulkAddTag}
            allowCreate
            contactOnly
            placeholder="Tag"
            showText={false}
          />
        </ButtonGroup>
      ),
    });
  }

  return (
    <SelectPopupContainer loading={loading}>
      <SelectionBarActions actions={actions} />

      {shouldDeleteCadences && (
        <DeleteCadencePopup onClose={() => setShouldDeleteCadences(false)} onDelete={() => onDelete()} />
      )}
      {shouldResolveEvents && (
        <MarkDonePopup onClose={() => setShouldResolveEvents(false)} onSubmit={() => onAcknowledge()} />
      )}
      {shouldDeleteReminders && (
        <MarkDonePopup onClose={() => setShouldDeleteReminders(false)} onSubmit={() => onDeleteReminders()} />
      )}
      {showExportModal && (
        <ExportContactsModal
          teamId={teamId}
          target={{ kind: 'selection', contactIds: selectedContactIds }}
          onClose={() => setShowExportModal(false)}
        />
      )}
      {enrollConfirm != null && (
        <BulkCampaignConfirm
          title={`Add ${contactIdsLength.toLocaleString()} people to ${enrollOwner == null ? 'this campaign' : campaignOf(enrollOwner)}?`}
          body={enrollTemplate == null ? undefined : `“${enrollTemplate.name}”`}
          owner={enrollOwner}
          acting={acting}
          senders={acting == null ? undefined : [{ person: acting, count: contactIdsLength }]}
          confirmLabel={`Add ${contactIdsLength.toLocaleString()}`}
          confirmColor="blue"
          onCancel={() => setEnrollConfirm(null)}
          onConfirm={() => {
            const id = enrollConfirm;
            setEnrollConfirm(null);
            void onBulkStartCadence(id);
          }}
          testId="bulk-enroll"
        />
      )}
    </SelectPopupContainer>
  );
};
